/**
 * Plugins & Skills — the org's connections and skill library, rendered as the
 * Amoreg product renders them: pill view switcher, one search field over the
 * active view, and the actions that view supports on the right.
 *
 * Follows the usage-page instant-open contract: the page chrome and the tab
 * shell paint immediately from a synchronous read of `useConnections`; data
 * fills in without a blocking spinner. See state/connections.ts.
 *
 * "Connect MCP" sits in the toolbar's right-hand corner on both tabs, for
 * every member: a personal server by default, or (for an admin) one for the
 * whole organization. Both kinds then list among the plugins.
 *
 * Every action here is gated on what the server says it can do — globally
 * (`capabilities`) and per connector (`connectable`) — so a button never
 * offers something the server cannot perform.
 *
 * @module components/connections/ConnectionsPage
 */
import { useNavigate, useParams } from "@tanstack/react-router";
import { RefreshCwIcon, SearchIcon } from "lucide-react";
import { useCallback, useState } from "react";

import { useStartChatWithText } from "../../hooks/useStartChatWithText";
import { useActiveOrgId } from "../../state/orgs";
import { useCustomMcpServers } from "../../state/customMcpServers";
import { ConnectMcpButton, openCustomMcpServer } from "../apps/integrations/ConnectMcpButton";
import { useRegulationOrg } from "../regulation/useRegulationOrg";
import { cn } from "../../lib/utils";
import {
  AnimatedTabs,
  AnimatedTabsContent,
  AnimatedTabsList,
  AnimatedTabsTrigger,
} from "../ui/animated-tabs";
import { Button } from "../ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../ui/input-group";
import { ConnectionsLoadError } from "./ConnectionsLoadError";
import { ConnectionsPageFrame } from "./ConnectionsPageFrame";
import { PluginsTab } from "./PluginsTab";
import type { PluginRowActions } from "./PluginRow";
import { CREATE_SKILL_PROMPT } from "./skillContent";
import { SkillsTab } from "./SkillsTab";
import { usePluginConnections } from "./usePluginConnections";

type Tab = "plugins" | "skills";

const TABS: ReadonlyArray<{ readonly id: Tab; readonly label: string }> = [
  { id: "plugins", label: "Plugins" },
  { id: "skills", label: "Skills" },
];

export function ConnectionsPage() {
  const [tab, setTab] = useState<Tab>("plugins");
  const [searchByTab, setSearchByTab] = useState({ plugins: "", skills: "" });
  const searchQuery = searchByTab[tab];
  const {
    connections,
    skills,
    capabilities,
    available,
    isPending,
    isRefreshing,
    error,
    refresh,
    actions,
  } = usePluginConnections();
  const navigate = useNavigate();
  // The tenant segment comes from the URL this page lives under, so a plugin
  // page can never open in a different organization than the catalogue.
  const orgSegment = useParams({
    strict: false,
    select: (params) => (params as { org?: string }).org ?? null,
  });

  // Custom MCP servers belong to the organization this page acts for: a
  // personal server's page lives under Plugins, the organization's under
  // Compliance → Integrations.
  const { org } = useRegulationOrg();
  const customOrgSegment = orgSegment ?? org?.slug ?? null;
  const { servers: customServers } = useCustomMcpServers(org?.id ?? null);
  const openCustomServer = useCallback(
    (serverId: string) => {
      if (customOrgSegment === null) return;
      const scope = customServers?.find((server) => server.id === serverId)?.scope;
      openCustomMcpServer(navigate, {
        scope: scope ?? "organization",
        org: customOrgSegment,
        serverId,
      });
    },
    [customOrgSegment, customServers, navigate],
  );

  const openPlugin = useCallback(
    (connectionId: string) => {
      if (orgSegment) {
        void navigate({
          to: "/$org/connections/$pluginId",
          params: { org: orgSegment, pluginId: connectionId },
        });
      } else {
        void navigate({ to: "/connections/$pluginId", params: { pluginId: connectionId } });
      }
    },
    [navigate, orgSegment],
  );

  // Writes the prompt into the org's new-chat draft, then opens it.
  const startChat = useStartChatWithText(useActiveOrgId());
  const createSkillWithAi = useCallback(() => {
    if (startChat) void startChat(CREATE_SKILL_PROMPT);
  }, [startChat]);

  const pluginActions: PluginRowActions = {
    ...actions,
    onOpen: openPlugin,
  };

  return (
    <AnimatedTabs
      value={tab}
      onValueChange={(value) => setTab(value as Tab)}
      className="h-full w-full gap-0"
    >
      <ConnectionsPageFrame
        toolbar={
          <div className="flex flex-wrap items-center gap-3 px-4 pt-4 sm:px-6 lg:px-8">
            <AnimatedTabsList
              noBorder
              aria-label="Plugins and skills"
              className="max-md:ml-[calc(var(--workspace-titlebar-control-size)+var(--workspace-titlebar-control-gap))]"
            >
              {TABS.map((item) => (
                <AnimatedTabsTrigger key={item.id} value={item.id}>
                  {item.label}
                </AnimatedTabsTrigger>
              ))}
            </AnimatedTabsList>

            <div className="order-3 w-full sm:order-none sm:mx-auto sm:max-w-sm lg:max-w-md">
              <div className="relative">
                <InputGroup className="h-8">
                  <InputGroupAddon className="text-muted-foreground">
                    <SearchIcon className="size-3.5" />
                  </InputGroupAddon>
                  <InputGroupInput
                    aria-label={`Search ${tab}`}
                    data-testid="connections-search"
                    placeholder={`Search ${tab}...`}
                    className="text-sm"
                    value={searchQuery}
                    onChange={(event) => {
                      const query = event.target.value;
                      setSearchByTab((current) => ({ ...current, [tab]: query }));
                    }}
                  />
                </InputGroup>
              </div>
            </div>

            <div className="ml-auto flex items-center gap-1">
              <ConnectMcpButton
                organizationId={org?.id ?? null}
                orgSegment={customOrgSegment}
                defaultScope="personal"
              />
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-muted-foreground"
                title="Reload the catalogue"
                disabled={isRefreshing}
                aria-busy={isRefreshing}
                onClick={refresh}
              >
                <RefreshCwIcon
                  className={cn(
                    "size-4",
                    isRefreshing && "animate-spin motion-reduce:animate-none",
                  )}
                />
                <span className="sr-only">Reload the catalogue</span>
              </Button>
            </div>
          </div>
        }
      >
        <div className="pb-6">
          {error ? (
            <div className="mb-4">
              <ConnectionsLoadError
                hasSnapshot={available}
                isRefreshing={isRefreshing}
                onRetry={refresh}
              />
            </div>
          ) : null}
          {!error || available ? (
            <>
              <AnimatedTabsContent value="plugins">
                <PluginsTab
                  actions={pluginActions}
                  available={available}
                  capabilities={capabilities}
                  connections={connections}
                  isPending={isPending}
                  searchQuery={searchQuery}
                  customServers={customServers ?? []}
                  onOpenCustomServer={customOrgSegment === null ? undefined : openCustomServer}
                />
              </AnimatedTabsContent>
              <AnimatedTabsContent value="skills">
                <SkillsTab
                  available={available}
                  isPending={isPending}
                  searchQuery={searchQuery}
                  skills={skills}
                  onCreateSkill={createSkillWithAi}
                  onSaved={refresh}
                  orgSegment={orgSegment}
                />
              </AnimatedTabsContent>
            </>
          ) : null}
        </div>
      </ConnectionsPageFrame>
    </AnimatedTabs>
  );
}
